// HeroSection.jsx - Updated with responsive improvements
import React, { useState, useEffect, useRef } from "react";
import { Button } from "../ButtonElements";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import {
  HeroContainer,
  HeroBg,
  ImgBg,
  HeroContent,
  HeroH1,
  HeroP,
  HeroBtnWrapper,
  ArrowForward,
  ArrowRight,
  GradientOverlay,
  FloatingShape,
  GlowingOrb,
  CodeSnippet,
  StatsContainer,
  StatItem,
  ScrollIndicator,
  ParticlesCanvas,
} from "./HeroSectionElements";
import Image from "../../images/cover.jpg";

// Motion variants
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] },
  },
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 1 } },
};

const scaleIn = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
};

// Floating animation
const floatingAnimation = {
  y: [0, -10, 0],
  transition: { duration: 3, repeat: Infinity, ease: "easeInOut" },
};

// Typing animation for code snippet
const codeLines = [
  "const creator = {",
  "  name: 'Fred Vuni',",
  "  experience: '5+ years building products & engineering solutions',",
  "  focus: 'Scalable, user-friendly applications',",
  "  passion: 'Bringing ideas to life through digital products',",
  "};",
];

const HeroSection = () => {
  const [isHovering, setIsHovering] = useState(false);
  const [currentLine, setCurrentLine] = useState(0);
  const [displayText, setDisplayText] = useState("");
  const [isTypingComplete, setIsTypingComplete] = useState(false);
  const [isMobile, setIsMobile] = useState(false); 
  const containerRef = useRef(null); 

  // Mouse parallax - disabled on mobile for better performance
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springConfig = { damping: 50, stiffness: 300 };
  const mouseXSpring = useSpring(mouseX, springConfig);
  const mouseYSpring = useSpring(mouseY, springConfig);
  const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], ["-3deg", "3deg"]);
  const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], ["3deg", "-3deg"]);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (isMobile) return; // Disable parallax on mobile

    const handleMouseMove = (e) => {
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      mouseX.set((clientX / innerWidth) * 2 - 1);
      mouseY.set((clientY / innerHeight) * 2 - 1);
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [mouseX, mouseY, isMobile]);

  // Typing effect with mobile optimization
  useEffect(() => {
    if (currentLine < codeLines.length) {
      const line = codeLines[currentLine];
      let charIndex = 0;
      const typingSpeed = isMobile ? 60 : 40; // Slower on mobile for better performance
      const typeInterval = setInterval(() => {
        if (charIndex <= line.length) {
          setDisplayText((prev) => {
            const lines = prev.split("\n");
            lines[currentLine] = line.substring(0, charIndex);
            return lines.join("\n");
          });
          charIndex++;
        } else {
          clearInterval(typeInterval);
          setCurrentLine((prev) => prev + 1);
          if (currentLine === codeLines.length - 1) setIsTypingComplete(true);
        }
      }, typingSpeed);
      return () => clearInterval(typeInterval);
    }
  }, [currentLine, isMobile]);

  return (
    <HeroContainer ref={containerRef}>
      <ParticlesCanvas />

      <HeroBg>
        <ImgBg
          as={motion.img}
          src={Image}
          alt="Developer workspace background"
          initial={{ scale: 1.1 }}
          animate={{ scale: [1.1, 1.2, 1.1] }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
          style={!isMobile ? { rotateX, rotateY } : {}}
        />
        <GradientOverlay />
      </HeroBg>

      {/* Floating decorations - hidden on mobile for better performance */}
      {!isMobile && (
        <>
          <FloatingShape
            as={motion.div}
            animate={floatingAnimation}
            style={{ top: "10%", left: "5%" }}
          >
            <GlowingOrb color="#4f46e5" size="120px" blur="50px" />
          </FloatingShape>

          <FloatingShape
            as={motion.div}
            animate={{ ...floatingAnimation, transition: { delay: 1 } }}
            style={{ bottom: "15%", right: "5%" }}
          >
            <GlowingOrb color="#ec4899" size="150px" blur="60px" />
          </FloatingShape>
        </>
      )}

      <HeroContent
        as={motion.div}
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
      >
        {/* Heading */}
        <HeroH1 as={motion.h1} variants={fadeUp}>
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            Creating digital solutions{" "}
          </motion.span>
          <motion.br />
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            Building reliable, user-focused
          </motion.span>
          <motion.br />
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            products & startups.
          </motion.span>
        </HeroH1>

        {/* Short description */}
        <HeroP as={motion.p} variants={fadeUp}>
          I help startups and products scale fast with clean, performant web
          solutions and thoughtful UX.
        </HeroP>

        {/* Code snippet - responsive with better mobile display */}
        <CodeSnippet
          as={motion.div}
          variants={scaleIn}
          whileHover={!isMobile ? { scale: 1.01 } : {}}
        >
          <pre>
            <code>
              {displayText || (isMobile ? "const creator = { ... }" : "")}
              {!isTypingComplete && !isMobile && (
                <motion.span
                  animate={{ opacity: [1, 0] }}
                  transition={{ duration: 0.8, repeat: Infinity }}
                  className="cursor"
                >
                  _
                </motion.span>
              )}
            </code>
          </pre>
        </CodeSnippet>

        {/* Button */}
        <HeroBtnWrapper as={motion.div} variants={fadeUp}>
          <Button
            to="projects"
            smooth={true}
            duration={500}
            spy={true}
            exact="true"
            offset={-80}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            primary="true"
            dark="true"
            whileHover={!isMobile ? { scale: 1.05 } : {}}
            whileTap={{ scale: 0.95 }}
          >
            View My Work
            <motion.span
              animate={{ x: isHovering ? 5 : 0 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              {isHovering ? <ArrowForward /> : <ArrowRight />}
            </motion.span>
          </Button>
        </HeroBtnWrapper>

        {/* Stats */}
        <StatsContainer
          as={motion.div}
          variants={fadeIn}
          initial="hidden"
          animate="visible"
        >
          <StatItem
            as={motion.div}
            whileHover={!isMobile ? { y: -5 } : {}}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <motion.span
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.0, duration: 0.5 }}
            >
              5+
            </motion.span>
            <p>Years</p>
          </StatItem>

          <StatItem
            as={motion.div}
            whileHover={!isMobile ? { y: -5 } : {}}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <motion.span
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.1, duration: 0.5 }}
            >
              50+
            </motion.span>
            <p>Projects</p>
          </StatItem>

          <StatItem
            as={motion.div}
            whileHover={!isMobile ? { y: -5 } : {}}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <motion.span
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.2, duration: 0.5 }}
            >
              100%
            </motion.span>
            <p>Satisfaction</p>
          </StatItem>
        </StatsContainer>
      </HeroContent>

      <ScrollIndicator
        to="about"
        smooth={true}
        duration={500}
        spy={true}
        offset={-80}
        as={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.5, duration: 0.5 }}
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          Scroll to explore
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 5v14M5 12l7 7 7-7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </motion.div>
      </ScrollIndicator>
    </HeroContainer>
  );
};

export default HeroSection;
